import dayjs from '~/timeInit';

import type { Dayjs } from '~/services/TimeService';

import type { VendorSummaryGranularity } from '~/types/services/vendor';

export const MONTH_FORMAT = 'YYYY-MM';
export const QUARTER_REGEX = /^(\d{4})-Q([1-4])$/;

export function quarterFromMonthIndex(monthIndex: number): 1 | 2 | 3 | 4 {
  return (Math.floor(monthIndex / 3) + 1) as 1 | 2 | 3 | 4;
}

export function parsePeriod(
  period: string,
  granularity: VendorSummaryGranularity,
): { start: Dayjs; end: Dayjs } | null {
  if (granularity === 'month') {
    const m = dayjs(period, MONTH_FORMAT, true);
    return m.isValid() ? { start: m.startOf('month'), end: m.endOf('month') } : null;
  }
  const match = QUARTER_REGEX.exec(period);
  if (!match) return null;
  const year = Number(match[1]);
  const quarter = Number(match[2]);
  const startMonth = (quarter - 1) * 3;
  const start = dayjs().year(year).month(startMonth).startOf('month');
  const end = start.add(2, 'month').endOf('month');
  return start.isValid() ? { start, end } : null;
}

export function formatPeriodForUrl(date: Dayjs, granularity: VendorSummaryGranularity): string {
  if (granularity === 'month') return date.format(MONTH_FORMAT);
  return `${date.year()}-Q${quarterFromMonthIndex(date.month())}`;
}

export function defaultPeriod(granularity: VendorSummaryGranularity): string {
  // Monthly defaults to the most recently *closed* month so we never show partial data.
  // Quarterly defaults to the quarter containing last month — which is the in-progress
  // current quarter for ~2 months out of every 3. That's intentional: market managers
  // want live visibility into how the active quarter is tracking against trailing-4Q
  // and same-Q-LY, matching Tableau KTR's behaviour. To force a closed-quarter default
  // we'd need to gate on whether today is past the quarter's end month.
  if (granularity === 'month') {
    return dayjs().subtract(1, 'month').startOf('month').format(MONTH_FORMAT);
  }
  const lastMonth = dayjs().subtract(1, 'month');
  return `${lastMonth.year()}-Q${quarterFromMonthIndex(lastMonth.month())}`;
}

export function periodLabel(period: string, granularity: VendorSummaryGranularity): string {
  const parsed = parsePeriod(period, granularity);
  if (!parsed) return period;
  if (granularity === 'month') return parsed.start.format('MMMM YYYY');
  const match = QUARTER_REGEX.exec(period);
  return match ? `Q${match[2]} ${match[1]}` : period;
}

// Short form (no year) for inline use in the email body, e.g. "* Q2: $744,600".
export function quarterShort(period: string): string {
  const match = QUARTER_REGEX.exec(period);
  return match ? `Q${match[2]}` : period;
}

// Deterministic — period shape is self-describing. `YYYY-MM` → month, `YYYY-Qn` → quarter.
// Used wherever we need to know the granularity but only have a period string in hand
// (e.g. the BE response no longer carries a separate `granularity` field).
export function granularityForPeriod(period: string): VendorSummaryGranularity {
  return QUARTER_REGEX.test(period) ? 'quarter' : 'month';
}

export function trailingAvgLabel(granularity: VendorSummaryGranularity, periodsInAvg: number | null): string {
  const periods = periodsInAvg ?? (granularity === 'month' ? 12 : 4);
  const unit = granularity === 'month' ? 'month' : 'quarter';
  return `${periods} ${unit} avg`;
}

export function currentPeriodLabel(granularity: VendorSummaryGranularity): string {
  return granularity === 'month' ? 'last month' : 'last quarter';
}

// Generate the picker options: a window of recent closed periods, most-recent first.
export function recentQuarterOptions(count = 12): Array<string> {
  const out: Array<string> = [];
  const cursor = dayjs().subtract(1, 'month');
  let q = quarterFromMonthIndex(cursor.month());
  let y = cursor.year();
  for (let i = 0; i < count; i += 1) {
    out.push(`${y}-Q${q}`);
    q -= 1;
    if (q < 1) {
      q = 4;
      y -= 1;
    }
  }
  return out;
}
